import { useState, useMemo, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Card, Text } from '@ui5/webcomponents-react';
import Container from '@mui/material/Container';
import useGetScoringData from '../hooks/useGetScoringData';
import { useTheme } from '../utils/ThemeContext';
import { ScoreBadge } from '../components/ScoreBadge';

const PLAYER_LABELS = ['Player 1', 'Player 2', 'Player 3', 'Player 4'];
const TOTAL_HOLES = 18;

function SummaryCard({ playerName, scores, pars, frontLabel = 'Front Nine', backLabel = 'Back Nine' }) {
  const { theme } = useTheme();
  const headerCell = { fontWeight: 'bold', color: '#fff', backgroundColor: theme.primary, padding: '4px 3px', fontSize: '0.7rem', textAlign: 'center', whiteSpace: 'nowrap' };
  const dataCell = { padding: '4px 3px', textAlign: 'center', fontSize: '0.75rem', color: theme.bodyText };
  const totalCell = { fontWeight: 'bold', padding: '4px 3px', textAlign: 'center', fontSize: '0.75rem', backgroundColor: theme.panelBgAlt };
  const tableWrapper = { overflowX: 'auto', WebkitOverflowScrolling: 'touch' };
  const front = scores.slice(0, 9);
  const back = scores.slice(9, 18);
  const frontPars = pars ? pars.slice(0, 9) : [];
  const backPars = pars ? pars.slice(9, 18) : [];
  const frontTotal = front.reduce((a, b) => a + b, 0);
  const backTotal = back.reduce((a, b) => a + b, 0);

  return (
    <Card style={{ marginBottom: '16px' }}>
      <Box pt={1.5} pb={1.5} pl={2} pr={2}>
        <Text style={{ fontWeight: 'bold', color: theme.primary, fontSize: '1rem', display: 'block', marginBottom: '10px' }}>
          {playerName}
        </Text>

        {/* Front nine */}
        <Text style={{ fontWeight: 'bold', color: theme.bodyText, fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>
          {frontLabel}
        </Text>
        <div style={{ ...tableWrapper, marginBottom: '12px' }}>
        <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              {front.map((_, i) => (
                <TableCell key={i} style={headerCell}>H{i + 1}</TableCell>
              ))}
              <TableCell style={headerCell}>Tot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {front.map((s, i) => (
                <TableCell key={i} style={dataCell}><ScoreBadge score={s} par={frontPars[i]} /></TableCell>
              ))}
              <TableCell style={totalCell}>{frontTotal}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        </div>

        {/* Back nine */}
        <Text style={{ fontWeight: 'bold', color: theme.bodyText, fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>
          {backLabel}
        </Text>
        <div style={tableWrapper}>
        <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              {back.map((_, i) => (
                <TableCell key={i} style={headerCell}>H{i + 1}</TableCell>
              ))}
              <TableCell style={headerCell}>Tot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {back.map((s, i) => (
                <TableCell key={i} style={dataCell}><ScoreBadge score={s} par={backPars[i]} /></TableCell>
              ))}
              <TableCell style={totalCell}>{backTotal}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        </div>
      </Box>
    </Card>
  );
}

export default function Tournament() {
  const { theme } = useTheme();
  const { members } = useGetScoringData();
  const [tourneyConfig, setTourneyConfig] = useState(null);
  const [frontCourse, setFrontCourse] = useState(null);
  const [backCourse, setBackCourse] = useState(null);
  const [players, setPlayers] = useState([null, null, null, null]);
  // holeScores[hole][playerIndex]
  const [holeScores, setHoleScores] = useState(
    Array.from({ length: TOTAL_HOLES }, () => [0, 0, 0, 0])
  );
  const [page, setPage] = useState(0); // 0 = setup, 1 = scoring, 2 = summary
  const [currentHole, setCurrentHole] = useState(0); // 0-indexed
  const [slideIn, setSlideIn] = useState(true);
  const [slideDir, setSlideDir] = useState('left');
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null); // null | 'success' | 'error'
  const [summaryFromHole, setSummaryFromHole] = useState(0);

  const tourneyRecords = tourneyConfig?.records ?? [];
  const tourneyFrontRec = tourneyRecords.find((r) => r.field === 'tournamentFront');
  const tourneyBackRec = tourneyRecords.find((r) => r.field === 'tournamentBack');
  const tourneyDateRec = tourneyRecords.find((r) => r.field === 'tournamentDate');

  const courseDate = tourneyDateRec?.dayValue ?? '';
  const frontCourseID = tourneyFrontRec?.paramValue ?? '';
  const backCourseID = tourneyBackRec?.paramValue ?? '';
  const frontCourseName = frontCourse?.courseName ?? tourneyFrontRec?.courseName ?? 'Front Nine';
  const backCourseName = backCourse?.courseName ?? tourneyBackRec?.courseName ?? 'Back Nine';

  const parValues = useMemo(() => {
    return Array.from({ length: TOTAL_HOLES }, (_, h) => {
      if (h < 9) {
        return frontCourse ? parseInt(frontCourse[`par_${h + 1}`], 10) || 4 : 4;
      } else {
        return backCourse ? parseInt(backCourse[`par_${h - 8}`], 10) || 4 : 4;
      }
    });
  }, [frontCourse, backCourse]);

  const parsInitialized = useRef(false);
  const visitedHoles = useRef(new Set());

  const visitHole = (holeIndex) => {
    if (visitedHoles.current.has(holeIndex)) return;
    visitedHoles.current.add(holeIndex);
    setHoleScores((prev) => {
      const updated = prev.map((h) => [...h]);
      const par = parValues[holeIndex];
      updated[holeIndex] = [par, par, par, par];
      return updated;
    });
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}api/config/readTourneyConfig.php`)
      .then((res) => res.json())
      .then((data) => setTourneyConfig(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!tourneyConfig) return;
    const records = tourneyConfig?.records ?? [];
    const frontID = records.find((r) => r.field === 'tournamentFront')?.paramValue;
    const backID = records.find((r) => r.field === 'tournamentBack')?.paramValue;
    if (frontID) {
      fetch(`${process.env.REACT_APP_API_URL}api/courses/read.php?courseID=${encodeURIComponent(frontID)}`)
        .then((res) => res.json())
        .then((data) => setFrontCourse(data?.records?.[0] ?? null))
        .catch(() => {});
    }
    if (backID) {
      fetch(`${process.env.REACT_APP_API_URL}api/courses/read.php?courseID=${encodeURIComponent(backID)}`)
        .then((res) => res.json())
        .then((data) => setBackCourse(data?.records?.[0] ?? null))
        .catch(() => {});
    }
  }, [tourneyConfig]);

  // Seed hole 0 to par when front course data first loads
  useEffect(() => {
    if (parsInitialized.current || !frontCourse) return;
    parsInitialized.current = true;
    visitHole(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontCourse, parValues]);

  const memberOptions = useMemo(() => {
    if (!members?.records) return [];
    return members.records.map((m) => ({ label: m.fullName, id: m.playerID }));
  }, [members]);

  const handleSelect = (index, newValue) => {
    setPlayers((prev) => {
      const updated = [...prev];
      updated[index] = newValue;
      return updated;
    });
  };

  const adjustScore = (playerIndex, delta) => {
    setHoleScores((prev) => {
      const updated = prev.map((h) => [...h]);
      updated[currentHole][playerIndex] = Math.max(0, updated[currentHole][playerIndex] + delta);
      return updated;
    });
  };

  const navigateHole = (delta) => {
    const dir = delta > 0 ? 'left' : 'right';
    setSlideDir(dir);
    setSlideIn(false);
    setTimeout(() => {
      setCurrentHole((h) => {
        const next = h + delta;
        visitHole(next);
        return next;
      });
      setSlideIn(true);
    }, 300);
  };

  const activePlayers = players
    .map((p, i) => ({ player: p, index: i }))
    .filter(({ player }) => player !== null);

  const allScoresEntered = activePlayers.every(({ index }) => holeScores[currentHole][index] > 0);

  const allScoresComplete = Array.from({ length: TOTAL_HOLES }, (_, h) => h)
    .every((h) => activePlayers.every(({ index }) => holeScores[h][index] > 0));

  const isLastHole = currentHole === TOTAL_HOLES - 1;

  const submitAll = async () => {
    setSubmitting(true);
    setSubmitResult(null);
    try {
      for (const { player, index } of activePlayers) {
        const playerScores = holeScores.map((hole) => hole[index]);
        // Front nine (holes 1-9)
        let front = 'playerID=' + player.id + '&course=' + frontCourseID + '&datePlayed=' + courseDate;
        front += '&isTourney=1&isFrontNine=1&otp=W9T5G4L';
        for (let h = 0; h < 9; h++) front += `&gross${h + 1}=${playerScores[h]}`;
        await fetch(`${process.env.REACT_APP_API_URL}api/scores/create.php`, {
          method: 'POST', body: front,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
        // Back nine (holes 10-18)
        let back = 'playerID=' + player.id + '&course=' + backCourseID + '&datePlayed=' + courseDate;
        back += '&isTourney=1&isFrontNine=0&otp=W9T5G4L';
        for (let h = 0; h < 9; h++) back += `&gross${h + 1}=${playerScores[h + 9]}`;
        await fetch(`${process.env.REACT_APP_API_URL}api/scores/create.php`, {
          method: 'POST', body: back,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
      }
      setSubmitResult('success');
    } catch (e) {
      setSubmitResult('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ overflow: 'hidden', position: 'relative' }}>
      {/* Page 0: Player Setup */}
      <Slide direction="right" in={page === 0} mountOnEnter unmountOnExit>
        <Container maxWidth="xs">
          <Box pt={4} pb={2} display="flex" justifyContent="center">
            <Text style={{ fontSize: '1.4rem', fontWeight: 'bold', color: theme.primary }}>
              Tournament Setup
            </Text>
          </Box>
          <Box display="flex" flexDirection="column" gap={2}>
            {PLAYER_LABELS.map((label, index) => (
              <Card key={index}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  pt={1.5}
                  pb={1.5}
                  pl={2}
                  pr={2}
                >
                  <Text style={{ fontWeight: 'bold', color: theme.primary, minWidth: '70px' }}>
                    {label}
                  </Text>
                  <Autocomplete
                    disablePortal
                    size="small"
                    options={memberOptions}
                    getOptionLabel={(option) => (option.label ? option.label : '')}
                    value={players[index]}
                    onChange={(event, newValue) => handleSelect(index, newValue)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    sx={{ width: 200 }}
                    renderInput={(params) => <TextField {...params} label="Member" />}
                  />
                </Box>
              </Card>
            ))}
          </Box>
          <Box display="flex" justifyContent="flex-end" pt={3} pb={4}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: theme.primary,
                '&:hover': { backgroundColor: theme.primaryHover },
                '&.Mui-disabled': { backgroundColor: theme.primaryDisabled, color: '#fff' },
              }}
              disabled={players.every((p) => p === null)}
              onClick={() => setPage(1)}
            >
              Next
            </Button>
          </Box>
        </Container>
      </Slide>

      {/* Page 1: Hole-by-hole Scoring */}
      <Slide direction="left" in={page === 1} mountOnEnter unmountOnExit>
        <Box>
          {/* Hole title */}
          <Box pt={4} pb={2} display="flex" justifyContent="center">
            <Slide direction={slideDir} in={slideIn}>
              <Box>
                <Text style={{ fontSize: '1.4rem', fontWeight: 'bold', color: theme.primary }}>
                  {currentHole < 9 ? frontCourseName : backCourseName} — Hole {currentHole < 9 ? currentHole + 1 : currentHole - 8}
                </Text>
              </Box>
            </Slide>
          </Box>

          {/* Player score cards */}
          <Container maxWidth="xs">
            <Slide direction={slideDir} in={slideIn}>
              <Box display="flex" flexDirection="column" gap={2}>
                {players.map((player, index) => {
                  if (!player) return null;
                  return (
                    <Card key={index}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        pt={1.5}
                        pb={1.5}
                        pl={1}
                        pr={1}
                      >
                        <Text style={{ fontWeight: 'bold', color: theme.primary, flex: 1, paddingLeft: '8px' }}>
                          {player.label}
                        </Text>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => adjustScore(index, -1)}
                            style={{ color: theme.primary }}
                          >
                            <RemoveCircleOutlineIcon />
                          </IconButton>
                          <TextField
                            value={holeScores[currentHole][index]}
                            size="small"
                            inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', width: '40px' } }}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              setHoleScores((prev) => {
                                const updated = prev.map((h) => [...h]);
                                updated[currentHole][index] = isNaN(val) ? 0 : Math.max(0, val);
                                return updated;
                              });
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => adjustScore(index, 1)}
                            style={{ color: theme.primary }}
                          >
                            <AddCircleOutlineIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            </Slide>

            {/* Navigation */}
            <Box display="flex" flexDirection="column" gap={1} pt={3} pb={4}>
              <Box display="flex" justifyContent="space-between">
                <Button
                  variant="outlined"
                  style={{ borderColor: theme.primary, color: theme.primary, minWidth: '48px' }}
                  onClick={() => currentHole === 0 ? setPage(0) : navigateHole(-1)}
                >
                  ←
                </Button>
                {isLastHole ? (
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: theme.primary,
                      '&:hover': { backgroundColor: theme.primaryHover },
                      '&.Mui-disabled': { backgroundColor: theme.primaryDisabled, color: '#fff' },
                    }}
                    disabled={!allScoresEntered}
                    onClick={() => { setSummaryFromHole(currentHole); setPage(2); }}
                  >
                    Finish
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: theme.primary,
                      minWidth: '48px',
                      '&:hover': { backgroundColor: theme.primaryHover },
                      '&.Mui-disabled': { backgroundColor: theme.primaryDisabled, color: '#fff' },
                    }}
                    disabled={!allScoresEntered}
                    onClick={() => navigateHole(1)}
                  >
                    →
                  </Button>
                )}
              </Box>
              <Box display="flex" justifyContent="center">
                <Button
                  variant="outlined"
                  style={{ borderColor: theme.primary, color: theme.primary }}
                  onClick={() => { setSummaryFromHole(currentHole); setPage(2); }}
                >
                  Scorecard
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </Slide>

      {/* Page 2: Summary */}
      <Slide direction="left" in={page === 2} mountOnEnter unmountOnExit>
        <Container maxWidth="sm">
          <Box pt={4} pb={2} display="flex" justifyContent="center">
            <Text style={{ fontSize: '1.4rem', fontWeight: 'bold', color: theme.primary }}>
              Score Summary
            </Text>
          </Box>
          <Box pb={4}>
            {activePlayers.map(({ player, index }) => (
              <SummaryCard
                key={index}
                playerName={player.label}
                scores={holeScores.map((hole) => hole[index])}
                pars={parValues}
                frontLabel={frontCourseName}
                backLabel={backCourseName}
              />
            ))}
          </Box>
          {submitResult === 'success' && (
            <Box pb={2} display="flex" justifyContent="center">
              <Text style={{ color: 'green', fontWeight: 'bold' }}>Scores submitted successfully!</Text>
            </Box>
          )}
          {submitResult === 'error' && (
            <Box pb={2} display="flex" justifyContent="center">
              <Text style={{ color: 'red', fontWeight: 'bold' }}>Submission failed. Please try again.</Text>
            </Box>
          )}
          <Box display="flex" justifyContent="space-between" pb={4}>
            <Button
              variant="outlined"
              style={{ borderColor: theme.primary, color: theme.primary }}
              disabled={submitting || submitResult === 'success'}
              onClick={() => { setPage(1); setCurrentHole(summaryFromHole); setSubmitResult(null); }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              sx={{
                backgroundColor: theme.primary,
                '&:hover': { backgroundColor: theme.primaryHover },
                '&.Mui-disabled': { backgroundColor: theme.primaryDisabled, color: '#fff' },
              }}
              disabled={submitting || submitResult === 'success' || !allScoresComplete}
              onClick={submitAll}
            >
              {submitting ? 'Submitting…' : 'Submit Scores'}
            </Button>
          </Box>
        </Container>
      </Slide>
    </Box>
  );
}
